import { useEffect, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { POLICY_EFFECTIVE_TH, POLICY_VERSION_LABEL } from '@shared/policy';
import { errorMessage } from '../api';
import { useAuth } from '../state/auth';
import { ErrorBox } from './ui';

/** Pages the user may read *through* the gate: there it collapses to a bottom bar instead of a blocking modal. */
const READABLE = new Set(['/privacy', '/terms']);

/**
 * Blocking consent gate: shown whenever a signed-in user has not accepted the current Privacy Policy / Terms
 * version (`me.policy_accepted === false`). Not dismissible — the only ways out are accepting or logging out.
 * Mounted once in the app shell above everything else.
 */
export function PolicyGate() {
  const { me, acceptPolicy, logout } = useAuth();
  const { path, route } = useLocation();
  const [privacy, setPrivacy] = useState(false);
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = !!me && !me.policy_accepted && me.role !== 'banned' && me.role !== 'deleted';
  const collapsed = open && READABLE.has(path);

  useEffect(() => {
    if (!open || collapsed) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open, collapsed]);

  if (!open) return null;

  if (collapsed) {
    return (
      <div class="policy-bar" role="region" aria-label="ยอมรับนโยบาย">
        <span class="small">อ่านจบแล้ว? กลับไปยอมรับเพื่อเริ่มใช้งาน</span>
        <button type="button" class="btn primary sm" onClick={() => (history.length > 1 ? history.back() : route('/'))}>
          กลับไปยอมรับ →
        </button>
      </div>
    );
  }

  const accept = async () => {
    setBusy(true);
    setError(null);
    try {
      await acceptPolicy();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="sheet-back policy-gate" role="presentation">
      <div class="sheet" role="dialog" aria-modal="true" aria-labelledby="policy-gate-title">
        <div class="grip" />
        <h2 id="policy-gate-title">ก่อนเริ่มใช้งาน</h2>
        <p class="muted small">
          {me?.display_name ? `สวัสดี ${me.display_name} — ` : ''}
          เราอัปเดตนโยบายความเป็นส่วนตัวและข้อกำหนดการใช้งาน (เวอร์ชัน {POLICY_VERSION_LABEL} · มีผล {POLICY_EFFECTIVE_TH})
          กรุณาอ่านและยอมรับก่อนใช้งานต่อ ลิงก์จะเปิดในแท็บใหม่
        </p>
        <div class="stack-8 mt-16">
          <label class="check card">
            <input type="checkbox" checked={privacy} onChange={(e) => setPrivacy((e.currentTarget as HTMLInputElement).checked)} />
            <span class="small">
              ฉันได้อ่านและยอมรับ{' '}
              <a href="/privacy" target="_blank" rel="noopener">
                นโยบายความเป็นส่วนตัว
              </a>{' '}
              — ข้อมูลที่เก็บ (จาก LINE: รหัสผู้ใช้ ชื่อ รูป) การเผยแพร่โปรไฟล์และรูปบนบอร์ดสาธารณะ และสิทธิ์ตาม PDPA
            </span>
          </label>
          <label class="check card">
            <input type="checkbox" checked={terms} onChange={(e) => setTerms((e.currentTarget as HTMLInputElement).checked)} />
            <span class="small">
              ฉันยอมรับ{' '}
              <a href="/terms" target="_blank" rel="noopener">
                ข้อกำหนดการใช้งานและกติกาชุมชน
              </a>{' '}
              — มาตามนัด เคารพเพื่อนร่วมตี้ ลงรูปเฉพาะตี้ที่เข้าร่วมและได้รับความยินยอมจากคนในรูป
            </span>
          </label>
        </div>
        {error && (
          <div class="mt-8">
            <ErrorBox message={error} />
          </div>
        )}
        <button type="button" class="btn primary lg block mt-16" disabled={!privacy || !terms || busy} onClick={accept}>
          {busy ? 'กำลังบันทึก…' : 'ยอมรับและเริ่มใช้งาน'}
        </button>
        <button type="button" class="btn ghost block mt-8" disabled={busy} onClick={() => void logout()}>
          ออกจากระบบ
        </button>
      </div>
    </div>
  );
}
